import { HOLIDAY_OFFSET, nextDate, nextDiwali, nextEaster,
  nextHanukkah, nextHoli, nextLunarNewYear, nextMardisGras, nextUsaThanksgiving } from './holidays';

export interface InspirationIdea {
  id: string;
  themes: InspirationTheme[];
  photoUrl: string;
  backgroundAnimationUrl?: string;
  backgroundAnimationSearchQuery?: string;
  title?: string;
  description?: string;
  weekday?: number;
  date?: Date;
  color?: string;
  keywords?: string[];
  prompt?: string;
  creator?: string;
}

export enum InspirationMainCategory {
  AdultBirthday = 500,
  KidsBirthday,
  Baby,
  Party,
  Wedding
}

export enum InspirationSubCategory {
  GeneralAdultBirthdays = 600,
  MilestoneAdultBirthdays,
  AdultBirthdayThemes,
  GeneralKidsBirthdays,
  KidsBirthdayThemes,
  MilestoneKidsBirthdays,
  BabyShower,
  SpecialtyShowers,
  Holidays,
  Entertaining,
  Quirky,
  Sports,
  WeddingShowers,
  WeddingWeekend
}

export enum InspirationTheme {
  _GeneralAdult = 700,
  Birthday_18,
  Birthday_21,
  Birthday_30,
  Birthday_40,
  Birthday_50,
  Birthday_60,
  Birthday_70,
  Birthday_75,
  Birthday_80,
  Birthday_90,
  Floral,
  Sports,
  FiestaBirthday,
  Dance,
  WesternBirthday,
  Birthday_70s,
  Birthday_80s,
  Beach,
  _GeneralKids,
  Birthday_1,
  BarMitzvah,
  Quinceanera,
  Sweet_16,
  Animals,
  Jump,
  Balloons,
  Gaming,
  Unicorns,
  Princess,
  Cake,
  Rainbows,
  Dinosaurs,
  WickedBirthday,
  BabyBoy,
  BabyGirl,
  GenderNeutral,
  Twins,
  BabySprinkle,
  GenderReveal,
  CouplesShower,
  SipAndSee,
  HundredDay,
  LunarNewYear,
  Valentines,
  Galentines,
  MardisGras,
  SaintPatricks,
  Holi,
  Easter,
  CincoDeMayo,
  July4th,
  Halloween,
  DiaDeLosMuertos,
  Thanksgiving,
  Friendsgiving,
  Holiday,
  Hanukkah,
  Diwali,
  NewYears,
  EntertainingGeneral,
  CocktailParty,
  DinnerParty,
  HappyHour,
  Brunch,
  WineNight,
  Barbecue,
  Bonfire,
  PoolParty,
  MovieNight,
  Sleepover,
  CraftNight,
  AnniversaryParty,
  Retirement,
  Graduation,
  Housewarming,
  LadiesNight,
  Picnic,
  MothersDay,
  FathersDay,
  Cubing,
  KiteDay,
  PieDay,
  CostumeParty,
  CandleMaking,
  FiestaQuirky,
  PizzaParty,
  TacoParty,
  GatsbyParty,
  WesternParty,
  TeaParty,
  Party_70s,
  Party_80s,
  Party_90s,
  SpringParty,
  SummerParty,
  Books,
  Winter,
  Masquerade,
  NeonAndGlow,
  CakeQuirky,
  WickedQuirky,
  BridalShower,
  Engagement,
  Bachelorette,
  Bachelor,
  RehearsalDinner,
  WeddingBrunch,
  Football,
  Soccer,
  Golf,
  Tennis,
  Baseball,
  Basketball,
  Bowling,
  GameNight,
  BookClub,
  MiniGolf,
  GoKarting,
  Hiking,
  SurpriseAdult,
  SurpriseKids
}

export interface InspirationMainCategoryDefinition {
  id: InspirationMainCategory,
  subCategories: InspirationSubCategoryDefinition[],
  label: string
}

export interface InspirationSubCategoryDefinition {
  id: InspirationSubCategory,
  themes: InspirationThemeDefinition[]
  label: string
}

export interface InspirationThemeDefinition {
  id: InspirationTheme,
  label: string;
  solo?: boolean;
}

export const InspirationCategories: InspirationMainCategoryDefinition[] = [
  {
    id: InspirationMainCategory.Party,
    label: 'Parties',
    subCategories: [
      {
        id: InspirationSubCategory.Holidays,
        label: 'Holidays',
        themes: [
          {
            id: InspirationTheme.LunarNewYear,
            label: 'Lunar New Year'
          },
          {
            id: InspirationTheme.Valentines,
            label: 'Valentine\'s Day'
          },
          {
            id: InspirationTheme.Galentines,
            label: 'Galentine\'s Day'
          },
          {
            id: InspirationTheme.MardisGras,
            label: 'Mardis Gras'
          },
          {
            id: InspirationTheme.SaintPatricks,
            label: 'St. Patrick\'s Day'
          },
          {
            id: InspirationTheme.Holi,
            label: 'Holi'
          },
          {
            id: InspirationTheme.Easter,
            label: 'Easter'
          },
          {
            id: InspirationTheme.CincoDeMayo,
            label: 'Cinco de Mayo'
          },
          {
            id: InspirationTheme.July4th,
            label: '4th of July'
          },
          {
            id: InspirationTheme.Halloween,
            label: 'Halloween'
          },
          {
            id: InspirationTheme.DiaDeLosMuertos,
            label: 'Dia de los Muertos'
          },
          {
            id: InspirationTheme.Thanksgiving,
            label: 'Thanksgiving'
          },
          {
            id: InspirationTheme.Friendsgiving,
            label: 'Friendsgiving'
          },
          {
            id: InspirationTheme.Holiday,
            label: 'Holiday'
          },
          {
            id: InspirationTheme.Hanukkah,
            label: 'Hanukkah'
          },
          {
            id: InspirationTheme.Diwali,
            label: 'Diwali'
          },
          {
            id: InspirationTheme.NewYears,
            label: 'New Years'
          }
        ]
      },
      {
        id: InspirationSubCategory.Entertaining,
        label: 'Entertaining & Celebrations',
        themes: [
          {
            id: InspirationTheme.EntertainingGeneral,
            label: 'General'
          },
          {
            id: InspirationTheme.AnniversaryParty,
            label: 'Anniversary Party'
          },
          {
            id: InspirationTheme.Barbecue,
            label: 'Barbecue'
          },
          {
            id: InspirationTheme.Bonfire,
            label: 'Bonfire'
          },
          {
            id: InspirationTheme.Brunch,
            label: 'Brunch'
          },
          {
            id: InspirationTheme.CocktailParty,
            label: 'Cocktail Party'
          },
          {
            id: InspirationTheme.CraftNight,
            label: 'Craft Night'
          },
          {
            id: InspirationTheme.DinnerParty,
            label: 'Dinner Party'
          },
          {
            id: InspirationTheme.FathersDay,
            label: 'Father\'s Day'
          },
          {
            id: InspirationTheme.Graduation,
            label: 'Graduation'
          },
          {
            id: InspirationTheme.HappyHour,
            label: 'Happy Hour'
          },
          {
            id: InspirationTheme.Housewarming,
            label: 'Housewarming'
          },
          {
            id: InspirationTheme.LadiesNight,
            label: 'Ladies Night'
          },
          {
            id: InspirationTheme.MothersDay,
            label: 'Mother\'s Day'
          },
          {
            id: InspirationTheme.MovieNight,
            label: 'Movie Night'
          },
          {
            id: InspirationTheme.Picnic,
            label: 'Picnic'
          },
          {
            id: InspirationTheme.PoolParty,
            label: 'Pool Party'
          },
          {
            id: InspirationTheme.Retirement,
            label: 'Retirement and Farewell'
          },
          {
            id: InspirationTheme.Sleepover,
            label: 'Sleepover'
          },
          {
            id: InspirationTheme.WineNight,
            label: 'Wine Night'
          }
        ]
      },
      {
        id: InspirationSubCategory.Quirky,
        label: 'Themed Parties',
        themes: [
          {
            id: InspirationTheme.Party_70s,
            label: '70s Party'
          },
          {
            id: InspirationTheme.Party_80s,
            label: '80s Party'
          },
          {
            id: InspirationTheme.Party_90s,
            label: '90s Party'
          },
          {
            id: InspirationTheme.Books,
            label: 'Books'
          },
          {
            id: InspirationTheme.CakeQuirky,
            label: 'Cake and Sweets'
          },
          {
            id: InspirationTheme.CandleMaking,
            label: 'Candle Making'
          },
          {
            id: InspirationTheme.CostumeParty,
            label: 'Costume Party'
          },
          {
            id: InspirationTheme.Cubing,
            label: 'Cubing Competition'
          },
          {
            id: InspirationTheme.FiestaQuirky,
            label: 'Fiesta'
          },
          {
            id: InspirationTheme.GatsbyParty,
            label: 'Gatsby Party'
          },
          {
            id: InspirationTheme.KiteDay,
            label: 'Kite Day'
          },
          {
            id: InspirationTheme.Masquerade,
            label: 'Masquerade'
          },
          {
            id: InspirationTheme.NeonAndGlow,
            label: 'Neon and Glow'
          },
          {
            id: InspirationTheme.PieDay,
            label: 'Pie Day'
          },
          {
            id: InspirationTheme.PizzaParty,
            label: 'Pizza Party'
          },
          {
            id: InspirationTheme.SpringParty,
            label: 'Spring'
          },
          {
            id: InspirationTheme.SummerParty,
            label: 'Summer'
          },
          {
            id: InspirationTheme.TacoParty,
            label: 'Taco Party'
          },
          {
            id: InspirationTheme.TeaParty,
            label: 'Tea Party'
          },
          {
            id: InspirationTheme.WesternParty,
            label: 'Western Party'
          },
          {
            id: InspirationTheme.WickedQuirky,
            label: 'Wicked'
          },
          {
            id: InspirationTheme.Winter,
            label: 'Winter'
          }
        ]
      },
      {
        id: InspirationSubCategory.Sports,
        label: 'Sports and Activities',
        themes: [
          {
            id: InspirationTheme.Football,
            label: 'Football'
          },
          {
            id: InspirationTheme.Soccer,
            label: 'Soccer'
          },
          {
            id: InspirationTheme.Golf,
            label: 'Golf'
          },
          {
            id: InspirationTheme.Tennis,
            label: 'Tennis & Pickleball'
          },
          {
            id: InspirationTheme.Baseball,
            label: 'Baseball'
          },
          {
            id: InspirationTheme.Basketball,
            label: 'Basketball'
          },
          {
            id: InspirationTheme.Bowling,
            label: 'Bowling'
          },
          {
            id: InspirationTheme.GameNight,
            label: 'Game Night'
          },
          {
            id: InspirationTheme.BookClub,
            label: 'Book Club'
          },
          {
            id: InspirationTheme.MiniGolf,
            label: 'Mini Golf'
          },
          {
            id: InspirationTheme.GoKarting,
            label: 'Go-Karting'
          },
          {
            id: InspirationTheme.Hiking,
            label: 'Hiking'
          }
        ]
      }
    ]
  },
  {
    id: InspirationMainCategory.AdultBirthday,
    label: 'Adult Birthday',
    subCategories: [
      {
        id: InspirationSubCategory.MilestoneAdultBirthdays,
        label: 'Milestone Birthdays',
        themes: [
          {
            id: InspirationTheme.Birthday_18,
            label: '18th Birthday'
          },
          {
            id: InspirationTheme.Birthday_21,
            label: '21st Birthday',
          },
          {
            id: InspirationTheme.Birthday_30,
            label: '30th Birthday'
          },
          {
            id: InspirationTheme.Birthday_40,
            label: '40th Birthday'
          },
          {
            id: InspirationTheme.Birthday_50,
            label: '50th Birthday'
          },
          {
            id: InspirationTheme.Birthday_60,
            label: '60th Birthday'
          },
          {
            id: InspirationTheme.Birthday_70,
            label: '70th Birthday'
          },
          {
            id: InspirationTheme.Birthday_75,
            label: '75th Birthday',
          },
          {
            id: InspirationTheme.Birthday_80,
            label: '80th Birthday'
          },
          {
            id: InspirationTheme.Birthday_90,
            label: '90th Birthday'
          }
        ]
      },
      {
        id: InspirationSubCategory.AdultBirthdayThemes,
        label: 'Birthday Themes',
        themes: [
          {
            id: InspirationTheme.Floral,
            label: 'Floral'
          },
          {
            id: InspirationTheme.Sports,
            label: 'Sports and Activities'
          },
          {
            id: InspirationTheme.FiestaBirthday,
            label: 'Fiesta'
          },
          {
            id: InspirationTheme.Dance,
            label: 'Dance and Music'
          },
          {
            id: InspirationTheme.WesternBirthday,
            label: 'Western'
          },
          {
            id: InspirationTheme.Birthday_70s,
            label: '70s Party'
          },
          {
            id: InspirationTheme.Birthday_80s,
            label: '80s Party'
          },
          {
            id: InspirationTheme.Beach,
            label: 'Beach and Tropical'
          },
          {
            id: InspirationTheme.SurpriseAdult,
            label: 'Surprise'
          }
        ]
      },
      {
        id: InspirationSubCategory.GeneralAdultBirthdays,
        label: 'General Birthdays',
        themes: [{
          id: InspirationTheme._GeneralAdult,
          label: '',
          solo: true
        }
        ]
      }
    ]
  },
  {
    id: InspirationMainCategory.KidsBirthday,
    label: 'Kids Birthday',
    subCategories: [
      {
        id: InspirationSubCategory.MilestoneKidsBirthdays,
        label: 'Milestone Birthdays',
        themes: [
          {
            id: InspirationTheme.Birthday_1,
            label: '1st Birthday'
          },
          {
            id: InspirationTheme.BarMitzvah,
            label: 'Bar and Bat Mitzvah'
          },
          {
            id: InspirationTheme.Quinceanera,
            label: 'Quinceañera'
          },
          {
            id: InspirationTheme.Sweet_16,
            label: 'Sweet 16'
          }
        ]
      },
      {
        id: InspirationSubCategory.KidsBirthdayThemes,
        label: 'Birthday Themes',
        themes: [
          {
            id: InspirationTheme.Animals,
            label: 'Animals'
          },
          {
            id: InspirationTheme.Jump,
            label: 'Jump'
          },
          {
            id: InspirationTheme.Balloons,
            label: 'Balloons and Confetti'
          },
          {
            id: InspirationTheme.Gaming,
            label: 'Gaming'
          },
          {
            id: InspirationTheme.Unicorns,
            label: 'Unicorns'
          },
          {
            id: InspirationTheme.Princess,
            label: 'Princess and Fairy'
          },
          {
            id: InspirationTheme.Cake,
            label: 'Cake and Sweets'
          },
          {
            id: InspirationTheme.Rainbows,
            label: 'Rainbows'
          },
          {
            id: InspirationTheme.Dinosaurs,
            label: 'Dinosaurs'
          },
          {
            id: InspirationTheme.WickedBirthday,
            label: 'Wicked'
          },
          {
            id: InspirationTheme.SurpriseKids,
            label: 'Surprise'
          }
        ]
      },
      {
        id: InspirationSubCategory.GeneralKidsBirthdays,
        label: 'General Kids Birthdays',
        themes: [{
          id: InspirationTheme._GeneralKids,
          label: '',
          solo: true
        }
        ]
      }
    ]
  },
  {
    id: InspirationMainCategory.Wedding,
    label: 'Wedding',
    subCategories: [
      {
        id: InspirationSubCategory.WeddingShowers,
        label: 'Showers & Parties',
        themes: [
          {
            id: InspirationTheme.BridalShower,
            label: 'Bridal Shower'
          },
          {
            id: InspirationTheme.Engagement,
            label: 'Engagement Party'
          },
          {
            id: InspirationTheme.Bachelorette,
            label: 'Bachelorette Party'
          },
          {
            id: InspirationTheme.Bachelor,
            label: 'Bachelor Party'
          }
        ]
      },
      {
        id: InspirationSubCategory.WeddingWeekend,
        label: 'Wedding Weekend',
        themes: [
          {
            id: InspirationTheme.RehearsalDinner,
            label: 'Rehearsal Dinner'
          },
          {
            id: InspirationTheme.WeddingBrunch,
            label: 'Wedding Brunch'
          }

        ]
      }
    ]
  },
  {
    id: InspirationMainCategory.Baby,
    label: 'Baby',
    subCategories: [
      {
        id: InspirationSubCategory.BabyShower,
        label: 'Baby Shower',
        themes: [
          {
            id: InspirationTheme.BabyBoy,
            label: 'Baby Boy'
          },
          {
            id: InspirationTheme.BabyGirl,
            label: 'Baby Girl'
          },
          {
            id: InspirationTheme.GenderNeutral,
            label: 'Gender-Neutral'
          },
          {
            id: InspirationTheme.Twins,
            label: 'Twins'
          }
        ]
      },
      {
        id: InspirationSubCategory.SpecialtyShowers,
        label: 'Specialty Showers',
        themes: [
          {
            id: InspirationTheme.BabySprinkle,
            label: 'Baby Sprinkle'
          },
          {
            id: InspirationTheme.GenderReveal,
            label: 'Gender Reveal'
          },
          {
            id: InspirationTheme.CouplesShower,
            label: 'Couples\' Shower'
          },
          {
            id: InspirationTheme.SipAndSee,
            label: 'Sip and See'
          },
          {
            id: InspirationTheme.HundredDay,
            label: '100 Day Celebrations'
          }
        ]
      }
    ]
  },
];

// Generate this with scripts/filter-themes.ts to optimize for which categories actually have data without having to
// wait for the data to load.
export const FilteredInspirationCategories: InspirationMainCategoryDefinition[] = [{'id':503,'label':'Parties','subCategories':[{'id':608,'label':'Holidays','themes':[{'id':743,'label':'Lunar New Year'},{'id':744,'label':'Valentine\'s Day'},{'id':745,'label':'Galentine\'s Day'},{'id':746,'label':'Mardis Gras'},{'id':747,'label':'St. Patrick\'s Day'},{'id':749,'label':'Easter'},{'id':750,'label':'Cinco de Mayo'},{'id':751,'label':'4th of July'},{'id':752,'label':'Halloween'},{'id':754,'label':'Thanksgiving'},{'id':755,'label':'Friendsgiving'},{'id':756,'label':'Holiday'},{'id':757,'label':'Hanukkah'},{'id':758,'label':'Diwali'},{'id':759,'label':'New Years'}]},{'id':609,'label':'Entertaining & Celebrations','themes':[{'id':760,'label':'General'},{'id':767,'label':'Bonfire'},{'id':761,'label':'Cocktail Party'},{'id':771,'label':'Craft Night'},{'id':762,'label':'Dinner Party'},{'id':779,'label':'Father\'s Day'},{'id':774,'label':'Graduation'},{'id':763,'label':'Happy Hour'},{'id':775,'label':'Housewarming'},{'id':776,'label':'Ladies Night'},{'id':778,'label':'Mother\'s Day'},{'id':769,'label':'Movie Night'},{'id':777,'label':'Picnic'},{'id':768,'label':'Pool Party'},{'id':770,'label':'Sleepover'},{'id':765,'label':'Wine Night'}]},{'id':610,'label':'Themed Parties','themes':[{'id':791,'label':'70s Party'},{'id':792,'label':'80s Party'},{'id':796,'label':'Books'},{'id':800,'label':'Cake and Sweets'},{'id':783,'label':'Costume Party'},{'id':785,'label':'Fiesta'},{'id':788,'label':'Gatsby Party'},{'id':781,'label':'Kite Day'},{'id':798,'label':'Masquerade'},{'id':799,'label':'Neon and Glow'},{'id':782,'label':'Pie Day'},{'id':786,'label':'Pizza Party'},{'id':794,'label':'Spring'},{'id':795,'label':'Summer'},{'id':787,'label':'Taco Party'},{'id':790,'label':'Tea Party'},{'id':789,'label':'Western Party'},{'id':801,'label':'Wicked'},{'id':797,'label':'Winter'}]},{'id':611,'label':'Sports and Activities','themes':[{'id':808,'label':'Football'},{'id':809,'label':'Soccer'},{'id':810,'label':'Golf'},{'id':811,'label':'Tennis & Pickleball'},{'id':812,'label':'Baseball'},{'id':813,'label':'Basketball'},{'id':814,'label':'Bowling'},{'id':815,'label':'Game Night'},{'id':817,'label':'Mini Golf'},{'id':818,'label':'Go-Karting'},{'id':819,'label':'Hiking'}]}]},{'id':500,'label':'Adult Birthday','subCategories':[{'id':602,'label':'Birthday Themes','themes':[{'id':711,'label':'Floral'},{'id':713,'label':'Fiesta'},{'id':715,'label':'Western'},{'id':716,'label':'70s Party'},{'id':717,'label':'80s Party'},{'id':718,'label':'Beach and Tropical'},{'id':820,'label':'Surprise'}]},{'id':600,'label':'General Birthdays','themes':[{'id':700,'label':'','solo':true}]}]},{'id':501,'label':'Kids Birthday','subCategories':[{'id':604,'label':'Birthday Themes','themes':[{'id':724,'label':'Animals'},{'id':725,'label':'Jump'},{'id':726,'label':'Balloons and Confetti'},{'id':727,'label':'Gaming'},{'id':728,'label':'Unicorns'},{'id':729,'label':'Princess and Fairy'},{'id':730,'label':'Cake and Sweets'},{'id':731,'label':'Rainbows'},{'id':732,'label':'Dinosaurs'},{'id':733,'label':'Wicked'},{'id':821,'label':'Surprise'}]},{'id':603,'label':'General Kids Birthdays','themes':[{'id':719,'label':'','solo':true}]}]},{'id':504,'label':'Wedding','subCategories':[{'id':612,'label':'Showers & Parties','themes':[{'id':802,'label':'Bridal Shower'},{'id':803,'label':'Engagement Party'},{'id':804,'label':'Bachelorette Party'}]},{'id':613,'label':'Wedding Weekend','themes':[{'id':807,'label':'Wedding Brunch'}]}]},{'id':502,'label':'Baby','subCategories':[{'id':606,'label':'Baby Shower','themes':[{'id':734,'label':'Baby Boy'},{'id':735,'label':'Baby Girl'},{'id':736,'label':'Gender-Neutral'},{'id':737,'label':'Twins'}]},{'id':607,'label':'Specialty Showers','themes':[{'id':739,'label':'Gender Reveal'},{'id':740,'label':'Couples\' Shower'}]}]}];

export interface CategoryMaps {
  main: Map<InspirationMainCategory, InspirationMainCategoryDefinition>,
  sub: Map<InspirationSubCategory, InspirationSubCategoryDefinition>,
  catSub: Map<InspirationSubCategory, InspirationMainCategoryDefinition>,
  catTheme: Map<InspirationTheme, InspirationMainCategoryDefinition>,
  subTheme: Map<InspirationTheme, InspirationSubCategoryDefinition>,
  theme: Map<InspirationTheme, InspirationThemeDefinition>
}

export const mapCategories = (mainCategories: InspirationMainCategoryDefinition[]): CategoryMaps => {
  const MappedMainCategories = new Map<InspirationMainCategory, InspirationMainCategoryDefinition>();
  const MappedSubCategories = new Map<InspirationSubCategory, InspirationSubCategoryDefinition>();
  const MainCategoriesForSub = new Map<InspirationSubCategory, InspirationMainCategoryDefinition>();
  const MainCategoriesForTheme = new Map<InspirationTheme, InspirationMainCategoryDefinition>();
  const SubCategoriesForTheme = new Map<InspirationTheme, InspirationSubCategoryDefinition>();
  const MappedThemes = new Map<InspirationTheme, InspirationThemeDefinition>();
  for (const mainCat of mainCategories) {
    MappedMainCategories.set(mainCat.id, mainCat);
    for (const subCat of mainCat.subCategories) {
      MappedSubCategories.set(subCat.id, subCat);
      MainCategoriesForSub.set(subCat.id, mainCat);
      for (const theme of subCat.themes) {
        MappedThemes.set(theme.id, theme);
        SubCategoriesForTheme.set(theme.id, subCat);
        MainCategoriesForTheme.set(theme.id, mainCat);
      }
    }
  }

  return {
    main: MappedMainCategories,
    sub: MappedSubCategories,
    catSub: MainCategoriesForSub,
    catTheme: MainCategoriesForTheme,
    subTheme: SubCategoriesForTheme,
    theme: MappedThemes
  };
};

const {
  main: MappedMainCategories,
  sub: MappedSubCategories,
  catSub: MainCategoriesForSub,
  catTheme: MainCategoriesForTheme,
  subTheme: SubCategoriesForTheme,
  theme: MappedThemes
} = mapCategories(FilteredInspirationCategories);

export const getInspirationById = async (id: string): Promise<InspirationIdea | undefined> => {
  const inspirations = await getInspirations();

  return inspirations.find((inspiration) => inspiration.id === id);
};

const getInspirations = async (): Promise<InspirationIdea[]> => {
  return (await import(
    /* webpackChunkName: "inspiration" */
    /* webpackPrefetch: true */
    '../data/inspiration.data'
    )).default;
};

export const mapInspirations = (inspirations: InspirationIdea[]): Map<InspirationTheme, InspirationIdea[]> => {
  const inspirationMap: Map<InspirationTheme, InspirationIdea[]> = new Map();
  for (const inspiration of inspirations) {
    for (const theme of inspiration.themes) {
      if (!inspirationMap.has(theme)) {
        inspirationMap.set(theme, []);
      }
      inspirationMap.get(theme)?.unshift(inspiration);

    }
  }

  return inspirationMap;
};

export const getInspirationsByType = async (): Promise<Map<InspirationTheme, InspirationIdea[]>> => {
  return mapInspirations(
    await getInspirations()
  );
};

export const getInspirationsById = async (): Promise<Map<string, InspirationIdea>> => {
  const inspirations = await getInspirations();

  const map = new Map<string, InspirationIdea>();
  inspirations.forEach(inspiration => {
    map.set(inspiration.id, inspiration);
  });
  return map;
};

const getThemeByLabel = (label: string): InspirationTheme | null => {
  for (const theme of MappedThemes.values()) {
    if (theme.label === label) {
      return theme.id;
    }
  }
  return null;
};

export const getInspirationsByTags = async (tags: string[]): Promise<InspirationIdea[]> => {
  const InspirationsByType: Map<InspirationTheme, InspirationIdea[]> = mapInspirations(await getInspirations());

  let inspirations: InspirationIdea[] = [];

  for (const tag of tags) {
    const theme = getThemeByLabel(tag);
    if (theme !== null) {
      inspirations = [...inspirations, ...(InspirationsByType.get(theme) ?? [])];
    }
  }

  return inspirations;
};

export const getFilteredInspirationsByType = async (tags: string[]): Promise<Map<InspirationTheme, InspirationIdea[]>> => {
  const inspirationMap: Map<InspirationTheme, InspirationIdea[]> = new Map();

  for (const inspiration of await getInspirationsByTags(tags)) {
    for (const theme of inspiration.themes) {
      if (!inspirationMap.has(theme)) {
        inspirationMap.set(theme, []);
      }
      inspirationMap.get(theme)?.unshift(inspiration);
    }
  }

  return inspirationMap;
};

export const getFilteredInspirationsBySearchTerm = async (searchTerm: string): Promise<Map<InspirationTheme, InspirationIdea[]>> => {
  const lcTerm = searchTerm.toLowerCase();
  return mapInspirations(
    (await getInspirations()).filter((inspiration) => {
      for (const theme of inspiration.themes) {
        if (getThemeLabel(theme)?.toLowerCase().includes(lcTerm)) {
          return true;
        }
      }

      return inspiration.title?.toLowerCase().includes(lcTerm) ||
        inspiration.description?.toLowerCase().includes(lcTerm) ||
        (inspiration.keywords && inspiration.keywords.indexOf(lcTerm) !== -1);
    })
  );
};

type HolidayTheme =
  InspirationTheme.Holiday
  | InspirationTheme.Hanukkah
  | InspirationTheme.Diwali
  | InspirationTheme.NewYears
  | InspirationTheme.LunarNewYear
  | InspirationTheme.Valentines
  | InspirationTheme.MardisGras
  | InspirationTheme.SaintPatricks
  // | InspirationTheme.Holi
  | InspirationTheme.Easter
  | InspirationTheme.CincoDeMayo
  | InspirationTheme.July4th
  | InspirationTheme.Halloween
  | InspirationTheme.DiaDeLosMuertos
  | InspirationTheme.Thanksgiving;

export const sortedHolidays = () => {
  const holidayDates = {
    [InspirationTheme.LunarNewYear]: nextLunarNewYear(HOLIDAY_OFFSET).getTime(),
    [InspirationTheme.Valentines]: nextDate(2, 14, HOLIDAY_OFFSET).getTime(),
    [InspirationTheme.MardisGras]: nextMardisGras(HOLIDAY_OFFSET).getTime(),
    [InspirationTheme.SaintPatricks]: nextDate(3, 17, HOLIDAY_OFFSET).getTime(),
    // [InspirationTheme.Holi]: nextHoli(HOLIDAY_OFFSET).getTime(),
    [InspirationTheme.Easter]: nextEaster(HOLIDAY_OFFSET).getTime(),
    [InspirationTheme.CincoDeMayo]: nextDate(5, 5, HOLIDAY_OFFSET).getTime(),
    [InspirationTheme.July4th]: nextDate(7, 4, HOLIDAY_OFFSET).getTime(),
    [InspirationTheme.Halloween]: nextDate(10, 31, HOLIDAY_OFFSET).getTime(),
    [InspirationTheme.DiaDeLosMuertos]: nextDate(11, 1, HOLIDAY_OFFSET).getTime(),
    [InspirationTheme.Thanksgiving]: nextUsaThanksgiving(HOLIDAY_OFFSET).getTime(),
    [InspirationTheme.Holiday]: nextDate(12, 25, HOLIDAY_OFFSET).getTime(),
    [InspirationTheme.Hanukkah]: nextHanukkah(HOLIDAY_OFFSET).getTime(),
    [InspirationTheme.Diwali]: nextDiwali(HOLIDAY_OFFSET).getTime(),
    [InspirationTheme.NewYears]: nextDate(12, 31, HOLIDAY_OFFSET).getTime(),
  };

  return ([
    InspirationTheme.Holiday,
    InspirationTheme.Hanukkah,
    InspirationTheme.Diwali,
    InspirationTheme.NewYears,
    InspirationTheme.LunarNewYear,
    InspirationTheme.Valentines,
    InspirationTheme.MardisGras,
    InspirationTheme.SaintPatricks,
    // InspirationTheme.Holi,
    InspirationTheme.Easter,
    InspirationTheme.CincoDeMayo,
    InspirationTheme.July4th,
    InspirationTheme.Halloween,
    InspirationTheme.DiaDeLosMuertos,
    InspirationTheme.Thanksgiving
  ] as HolidayTheme[]).sort((a: HolidayTheme, b: HolidayTheme) => holidayDates[a] - holidayDates[b]);
};

export const getThemeLabel = (themeId: InspirationTheme): string | undefined => {
  const theme = MappedThemes.get(themeId);
  if (theme?.solo) {
    return SubCategoriesForTheme.get(themeId)?.label;
  } else {
    return theme?.label;
  }
};

export const getMainCategory = (mainCatId: InspirationMainCategory) => {
  return MappedMainCategories.get(mainCatId);
};

export const getThemeMainCategory = (themeId: InspirationTheme,
                                     catMap?: CategoryMaps): InspirationMainCategoryDefinition | undefined => {
  if (catMap === undefined) {
    return MainCategoriesForTheme.get(themeId);
  } else {
    return catMap.catTheme.get(themeId);
  }
};

export const getThemeSubCategory = (themeId: InspirationTheme,
                                    catMap?: CategoryMaps): InspirationSubCategoryDefinition | undefined => {
  if (catMap === undefined) {
    return SubCategoriesForTheme.get(themeId);
  } else {
    return catMap.subTheme.get(themeId);
  }
};

export const getNextHolidayTheme = (): InspirationThemeDefinition => {
  const holidays = sortedHolidays();

  return MappedThemes.get(holidays[0]) as InspirationThemeDefinition;
};

export const getExampleThemes = (): InspirationThemeDefinition[] => {
  const holidays = sortedHolidays();

  const themes: InspirationThemeDefinition[] = [];
  const nextHoliday = MappedThemes.get(holidays[0]);
  if (nextHoliday) {
    themes.push(nextHoliday);
  }

  return themes;
};
